import type { ServiceName } from 'src/components/loginHandler/LoginContext';

import { LOGIN_SCOPES, type TokenScope } from '../../../constants';
import { clearCombinedToken as clearAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import {
    isInstanceOverrideCode,
    setAvailableEvoInstances,
    type Hub,
    type Org,
} from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { BentleyEntitlementsApiClient } from '../BentleyEntitlementsApiClient';
import { FAKE_LOCAL_BMS_LICENSE_ACCESS } from './fakeLocalBmsLicenceAccess';

export type Discovery = {
    hubs: Array<Hub>;
    services: Array<{ code: string; display_name: string }>;
    organizations: Array<Org>;
    service_access: Array<{ hub_code: string; org_id: string; services: string[] }>;
    has_new_instances?: boolean;
};

// Helper function to build the service query params for the license orgs & access requests that can have
// multiple service query params when there are multiple services, e.g. service=service1&service=service2
// Because the RTK uses URLSearchParams, which generates output like service=service1,service2, that has
// param serialization incompatible with a FastAPI backend handling of arrays.
function buildServiceParams(service: ServiceName | ServiceName[]) {
    return (Array.isArray(service) ? service : [service])
        .map((s) => `service=${encodeURIComponent(s)}`)
        .join('&');
}

function matchEvoInstances(discovery: Discovery, service: ServiceName | ServiceName[]) {
    const hubs = new Map(discovery.hubs.map((h) => [h.code, h]));
    const orgs = new Map(discovery.organizations.map((o) => [o.id, o]));
    const services = Array.isArray(service) ? service : [service];

    return discovery.service_access
        .filter((sa) => services.some((s) => sa.services.includes(s)))
        .map((sa) => ({ hub: hubs.get(sa.hub_code), org: orgs.get(sa.org_id) }))
        .filter((inst): inst is { hub: Hub; org: Org } => !!inst.hub && !!inst.org);
}

// Clears the access token on HTTP 4xx+ errors (but not on fetch/network errors
// like cancelled requests) and returns the error in RTK Query format.
function handleDiscoveryError(error: any) {
    const { status } = error;
    if (status && typeof status === 'number' && status >= 400) {
        clearAccessToken();
    }
    return { error };
}

export const BentleyEntitlementsApi = BentleyEntitlementsApiClient.injectEndpoints({
    endpoints: (build) => ({
        loadBentleyLicenseOrgs: build.query<
            Discovery,
            { service: ServiceName | ServiceName[]; combineResponses?: boolean }
        >({
            async queryFn({ service, combineResponses }, _queryApi, _extraOptions, baseQuery) {
                const serviceParams = buildServiceParams(service);
                const discoveryResponse = await baseQuery({
                    url: `/evo/identity/v2/discovery?${serviceParams}`,
                    // combineResponses must be in params (not the URL string) so that
                    // createBentleyAccessTokenBaseQuery can detect it and make the dual
                    // dev + integration discovery calls.
                    ...(combineResponses && { params: { combineResponses } }),
                });

                if (discoveryResponse.error) {
                    return handleDiscoveryError(discoveryResponse.error);
                }

                const discovery = (discoveryResponse.data as { discovery: Discovery })?.discovery;
                if (!discovery) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'Error: No licenses' } };
                }

                if (!discovery.service_access.length) {
                    setAvailableEvoInstances([]);
                    return { data: discovery };
                }

                const availableEvoInstances = matchEvoInstances(discovery, service);
                setAvailableEvoInstances(availableEvoInstances);

                if (availableEvoInstances.length === 0) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: `Error: No valid org/hub for ${typeof service === 'string' ? service : service.join(' ')}`,
                        },
                    };
                }
                return { data: discovery };
            },
        }),

        loadBentleyLicenseAccess: build.query<
            any,
            {
                service: ServiceName | ServiceName[];
                hubCode: string;
                orgId: string;
                requiredScope: TokenScope[];
                combineResponses?: boolean;
                hubUrl?: string;
            }
        >({
            async queryFn(
                { service, hubCode, orgId, requiredScope, combineResponses, hubUrl },
                _queryApi,
                _extraOptions,
                baseQuery,
            ) {
                if (requiredScope.length === 0) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Error: Required scopes is missing',
                        },
                    };
                } else {
                    const invalidScope = requiredScope.find(
                        (scope) => !LOGIN_SCOPES.includes(scope),
                    );
                    if (invalidScope) {
                        return {
                            error: {
                                status: 'CUSTOM_ERROR',
                                error: `Error: ${invalidScope} scope is invalid`,
                            },
                        };
                    }
                }

                if (orgId === '') {
                    return {
                        error: { status: 'CUSTOM_ERROR', error: 'Error: Org id is unprovided' },
                    };
                }

                if (hubCode === '') {
                    return {
                        error: { status: 'CUSTOM_ERROR', error: 'Error: Hub code is unprovided' },
                    };
                }

                if (isInstanceOverrideCode(hubCode)) {
                    // Return fake data so we don't make a failed request
                    return {
                        data: FAKE_LOCAL_BMS_LICENSE_ACCESS(orgId, hubCode),
                    };
                }

                const requiredScopes = requiredScope
                    .map((scope) => `required_scope=${encodeURIComponent(scope)}`)
                    .join('&');
                // Generate the requests' query params, which may have multiple service query params, so that there are not a mix
                // use of URL params and RTK _param_ serialization.
                const serviceParams = buildServiceParams(service);
                const instanceParams = `hub=${encodeURIComponent(hubCode)}&org_id=${encodeURIComponent(orgId)}`;
                // combineResponses and hubUrl must be in params (not the URL string) so that
                // createBentleyAccessTokenBaseQuery can detect them and route to the correct
                // dev or integration endpoint.
                const licenseAccessResponse = await baseQuery({
                    url: hubUrl
                        ? `${hubUrl}/workspace/evo/identity/v2/license-access?${requiredScopes}&${serviceParams}&${instanceParams}`
                        : `/evo/identity/v2/license-access?${requiredScopes}&${serviceParams}&${instanceParams}`,
                    ...(combineResponses && {
                        params: {
                            combineResponses,
                            ...(hubUrl && { hubUrl }),
                        },
                    }),
                });

                if (licenseAccessResponse.error) {
                    return { error: licenseAccessResponse.error };
                }

                return licenseAccessResponse;
            },
        }),
    }),
});

export const {
    useLoadBentleyLicenseOrgsQuery,
    useLazyLoadBentleyLicenseOrgsQuery,
    useLoadBentleyLicenseAccessQuery,
} = BentleyEntitlementsApi;
